/**
 * Consolidate logic for dynamic routing here.
 *
 * Instead of having history.push() logic scattered
 * all around the codebase, let's try consolidating
 * that logic here.
 *
 * We are using react-router for client side routing,
 * here, we need to ensure that we are importing and
 * using the app wide history object.
 */
import history from 'config/history';
import { getBessieLink } from 'services/warehouse/api';
import { bugsnagError } from 'services/bugsnag';
import { WAREHOUSE_CLIENT_URL } from 'config/endpoints';

/** Link paths. */
export const modsyHelpUrl = 'https://help.modsy.com';
export const accountPageUrl = '/account';
export const accountLandingUrl = '/account/settings';
export const orderHistoryUrl = '/account/order-history';
export const expressDesignGenerationUrl = '/express/design-generation';
export const expressRoomDetailsUrl = '/express/room-details';
export const expressUpgradeUrl = '/express/upgrade';
export const expressUpgradeCheckoutUrl = '/express/checkout';
export const termsOfServiceUrl = '/terms-of-service';
export const shopCatalogUrl = '/shop';
export const talkablePostPurchaseStandaloneUrl = '/friend';
export const talkableDashboardUrl = '/referrals';
export const howItWorksUrl = 'https://www.modsy.com/how-it-works/';
export const examplesUrl = 'https://www.modsy.com/real-interior-design-stories/';
export const styleQuizUrl = 'https://www.modsy.com/design-style-quiz/';
export const faqUrl = 'https://www.modsy.com/online-design-package-faqs/';
export const pricingUrl = 'https://www.modsy.com/online-interior-design-packages/';
export const renovationPackagesUrl = '/renovation-package-page/';
export const designIdeasUrl = '/design-ideas/';
export const designIdeasBedroomUrl = '/design-ideas/bedroom';
export const diningRoomUrl = '/design-ideas/dining-room';
export const homeOfficeUrl = '/design-ideas/home-office';
export const kidsRoomUrl = '/design-ideas/kids-room';
export const livingRoomUrl = '/design-ideas/living-room';
export const giftCardsUrl = '/modsy-gift-cards/';
export const blogUrl = 'https://blog.modsy.com/';
export const homeDesignBlogUrl = 'https://blog.modsy.com/home-design-tips-guides';
export const realOnlineInteriorBlogUrl = 'https://blog.modsy.com/real-online-interior-design-stories';
export const styleIdeasBlogUrl = 'https://blog.modsy.com/style-ideas';
export const trendsBlogUrl = 'https://blog.modsy.com/trends';
export const layoutGuidesBlogUrl = 'https://blog.modsy.com/home-design-tips-guides/layout-guides';
export const onlineVsTraditionalDesignUrl = '/online-interior-design-versus-traditional';
export const online3DDesignUrl = '/3d-online-design-difference';
export const expressHowItWorksUrl = '/express#how-it-works';
export const expressPricingUrl = '/express#pricing';
export const expressStartProjectUrl = '/express#pricing';
export const startProjectUrl = '/project/room';
export const loginUrl = '/login';
export const logoutUrl = '#';
export const customerLandingPage = '/projects';
export const buyGiftCardUrl = '/modsy-gift-cards/packages';
export const giftCardInfo = '/modsy-gift-cards/information';
export const giftCardThankYouUrl = '/gift-card/thankyou';

/** Client side dynamic routing push events. */
export const navigateToHomePage = ({ search } = {}) => {
  history.push({
    pathname: '/',
    search: search !== undefined ? search : window.location.search
  });
};

export const navigateToLoginPage = (next) => {
  let search = window.location.search;
  if (next) {
    search += `${search ? '&' : '?'}next=${next}`;
  }

  history.push({
    pathname: '/login',
    search
  });
};

export const navigateToStartNewSpace = ({ partnerSlug } = {}) => {
  history.push({
    pathname: '/project/room',
    search: partnerSlug ? `?prtnr=${partnerSlug}` : ''
  });
};

export const navigateToProjectRecommendation = () => {
  history.push({
    pathname: '/project/your-recommendation',
    search: window.location.search
  });
};

export const navigateToProjectThankYouFor = ({ spaceId }) => {
  history.push({
    pathname: `/project/thankyou/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToCustomPieces = ({ spaceId }) => {
  history.push({
    pathname: `/spaces/${spaceId}/custom-pieces`,
    search: window.location.search
  });
};

export const navigateToCentralHubFor = ({ spaceId }) => {
  history.push({
    pathname: `/spaces/${spaceId}/dashboard`,
    search: window.location.search
  });
};

export const navigateToProjectSubmittedFor = ({ spaceId }) => {
  history.push({
    pathname: `/spaces/${spaceId}/dashboard/magic-is-happening`,
    search: window.location.search
  });
};

export const navigateToAccount = ({ search } = {}) => {
  history.push({
    pathname: accountPageUrl,
    search: search !== undefined ? search : window.location.search
  });
};

export const navigateToAccountLandingPage = ({ search = '' } = {}) => {
  history.push({
    pathname: accountLandingUrl,
    search: search || window.location.search
  });
};

export const navigateToStudio = () => {
  history.push({
    pathname: '/studio',
    search: window.location.search
  });
};

export const navigateToStudioFor = ({ spaceId, viewType, noRedirect = false }) => {
  const vt = `${viewType ? `/${viewType}` : ''}`;
  const pathname = `/studio${vt}/${spaceId}`;

  history.push({
    pathname,
    search: window.location.search,
    state: { noRedirect }
  });
};

export const navigateToPPOFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO dashboard', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/dashboard`,
    search: window.location.search
  });
};

// PPO step 1 - unique page
export const navigateToAboutYouFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO about-you step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/about-you`,
    search: window.location.search
  });
};

// PPO step 2 - unique page
export const navigateToProjectSummaryFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO project-summary step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/project-summary`,
    search: window.location.search
  });
};

// PPO step 3 - first page
export const navigateToRoomDetailsRenovationsFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO room-details/renovations step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/room-details/renovations`,
    search: window.location.search
  });
};

// PPO step 3 - last page
export const navigateToRoomDetailsFloorPlanFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO room-details/floor-plan step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/room-details/floor-plan`,
    search: window.location.search
  });
};

// PPO step 4 - first page
export const navigateToRoomStyleFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO stylequiz/rooms step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/stylequiz/rooms`,
    search: window.location.search
  });
};

// PPO step 5 - first page
export const navigateToProjectVisionFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO vision/space-usage step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/vision/space-usage`,
    search: window.location.search
  });
};

// PPO step 5 - last page
export const navigateToVisionNoteFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO /vision/notes step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/vision/notes`,
    search: window.location.search
  });
};

// PPO step 6 - first page
export const navigateToExistingFurnitureFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to PPO custom-pieces step', { spaceId });
  }

  history.push({
    pathname: `/spaces/${spaceId}/custom-pieces`,
    search: window.location.search
  });
};

export const navigateToProjectPageFor = ({ spaceId }) => {
  if (!spaceId) {
    bugsnagError('Error spaceId is falsey when navigating to customer project page', { spaceId });
  }

  // This handles the case where we checkout and need to redirect to the project page while working on local.
  // This is because checkout and projects are two separate apps
  if (window.location.hostname === 'localhost') {
    return window.location.assign(`${WAREHOUSE_CLIENT_URL}/projects/${spaceId}`);
  }

  window.location.assign(`/projects/${spaceId}`);
  return null;
};

/* Mobile Only - Navigate to Studio Products Page */
export const navigateToStudioProducts = ({ spaceId, designId, viewType }) => {
  const pathname = `/studio${viewType ? `/${viewType}` : ''}/${spaceId}/design/${designId}/products`;
  history.push({
    pathname,
    search: window.location.search
  });
};

/* Mobile Only - Navigate to Studio Product Favorites Page */
export const navigateToStudioFavorites = ({ spaceId, designId }) => {
  const pathname = `/studio/${spaceId}/design/${designId}/favorites`;
  history.push({
    pathname,
    search: window.location.search
  });
};

/* Mobile Only - Navigate to Studio Alternative Products Page */
export const navigateToStudioSelectedProduct = ({ spaceId, designId, productId }) => {
  const pathname = `/studio/${spaceId}/design/${designId}/products/${productId}`;
  history.push({
    pathname,
    search: window.location.search
  });
};

/* Mobile Only - Navigate to Studio Alternative Products Page from Hotspot */
export const navigateToStudioSelectedHotspot = ({ spaceId, designId, productId, viewType }) => {
  const pathname = `/studio${viewType ? `/${viewType}` : ''}/${spaceId}/design/${designId}/hotspot/${productId}`;
  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToStudioSpaceAndDesignFor = ({ spaceId, designId, viewType, replace, doNotScrollTop, search, showSummary }) => {
  const historyAction = replace ? 'replace' : 'push';
  const pathname = `/studio${viewType ? `/${viewType}` : ''}/${spaceId}/design/${designId}${showSummary ? '/summary' : ''}`;

  history[historyAction]({
    pathname,
    search: search !== undefined ? search : window.location.search,
    state: { doNotScrollTop: !!doNotScrollTop }
  });
};

export const navigateToStudioDesignNotesFor = ({ spaceId, designId, viewType }) => {
  const vt = viewType ? `/${viewType}` : '';
  const pathname = `/studio${vt}/${spaceId}/design/${designId}/design-notes`;
  history.push({
    pathname,
    search: window.location.search,
    state: { doNotScrollTop: true }
  });
};

export const navigateToStudioPDPFor = ({ spaceId, designId, productId, viewType }) => {
  const vt = viewType ? `/${viewType}` : '';
  const pathname = `/studio${vt}/${spaceId}/design/${designId}/product/${productId}`;

  history.push({
    pathname,
    search: window.location.search,
    // TODO: using to manage FullScreenRender Index
    // need clearer way to do this
    state: { onPDPpage: true, doNotScrollTop: true }
  });
};

export const navigateToStudioCartFor = ({ spaceId, designId, viewType }) => {
  const vt = `${viewType ? `/${viewType}` : ''}`;
  const pathname = `/studio${vt}/${spaceId}/design/${designId}/cart`;

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToPPOCartFor = ({ spaceId }) => {
  const pathname = `/spaces/${spaceId}/cart`;

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToStudioCartThankYouFor = ({ spaceId, designId, viewType }) => {
  const vt = `${viewType ? `/${viewType}` : ''}`;
  const pathname = `/studio${vt}/${spaceId}/design/${designId}/cart/thankyou`;

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToPPOCartThankYouFor = ({ spaceId }) => {
  history.push({
    pathname: `/spaces/${spaceId}/cart/thankyou`,
    search: window.location.search
  });
};

export const navigateToRedesign = ({ spaceId, designId }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign`,
    search: window.location.search
  });
};

/* Give the user a chance to select the design first */
export const navigateToSpaceRedesign = ({ spaceId }) => {
  history.push({
    pathname: `/studio/${spaceId}/redesign`,
    search: window.location.search
  });
};

export const navigateToRedesignStep = ({ spaceId, designId, redesignStep }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign/${redesignStep}`,
    search: window.location.search
  });
};

export const navigateToSwapProducts = ({ spaceId, designId }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign/swap-products`,
    search: window.location.search
  });
};

export const navigateToSwapProductsBrowser = ({ spaceId, designId, productId }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign/swap-products/${productId}/product-browser`,
    search: window.location.search
  });
};

export const navigateToSwapProductsPDP = ({ spaceId, designId, originalProductId, productId }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign/swap-products/${originalProductId}/product/${productId}`,
    search: window.location.search
  });
};

export const navigateToAddProductsPDP = ({ spaceId, designId, productId }) => {
  history.push({
    pathname: `/studio/${spaceId}/design/${designId}/redesign/product/${productId}`,
    search: window.location.search
  });
};

export const navigateToCartPDP = ({ spaceId, designId, productId, viewType }) => {
  const vt = `${viewType ? `/${viewType}` : ''}`;
  const pathname = `/studio${vt}/${spaceId}/design/${designId}/cart/product/${productId}`;

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToBessie = ({ spaceId, designIndex, queryParam }) => {
  getBessieLink(spaceId, designIndex).then(bessieLink => {
    history.push(`${bessieLink}${queryParam || ''}`);
  });
};

export const navigateToOrderHistory = () => {
  window.location.href = '/account/order-history';
};

export const navigateToOrderDetails = orderId => {
  window.location.href = `/account/order-history/order-details/${orderId}`;
};

export const navigateToStudioSummary = ({ spaceId, designId, viewType }) => {
  let pathname = `/studio${viewType ? `/${viewType}` : ''}/${spaceId}`;
  if (designId) {
    pathname += `/design/${designId}`;
  }
  pathname += '/summary';

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToEmailVerified = () => {
  history.push({
    pathname: '/account/settings?emailVerified=true',
    search: window.location.search
  });
};

export const navigateTo404 = () => {
  history.push({
    pathname: '/404',
    search: window.location.search
  });
};

export const navigateToExpressRoomDetails = ({ spaceId, search, replace }) => {
  const fcn = replace ? history.replace : history.push;
  fcn({
    pathname: `${expressRoomDetailsUrl}/${spaceId}`,
    search: (search !== undefined) ? search : window.location.search
  });
};

export const navigateToExpressStyleRooms = ({ spaceId }) => {
  history.push({
    pathname: `/express/style-rooms/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressStyleProducts = ({ spaceId }) => {
  history.push({
    pathname: `/express/style-products/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressStyleResult = ({ spaceId }) => {
  history.push({
    pathname: `/express/style-result/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressProjectDetails = ({ spaceId }) => {
  history.push({
    pathname: `/express/project-details/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressGeneratingDesign = ({ spaceId, replace }) => {
  const historyAction = replace ? 'replace' : 'push';
  history[historyAction]({
    pathname: `${expressDesignGenerationUrl}/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressDesignReady = ({ spaceId }) => {
  history.push({
    pathname: `/express/design-ready/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressGetStarted = () => {
  history.push({
    pathname: '/express/get-started',
    search: window.location.search
  });
};

export const navigateToExpressUpgrade = ({ spaceId }) => {
  history.push({
    pathname: `/express/upgrade/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressUpgradeCheckout = ({ spaceId }) => {
  history.push({
    pathname: `/express/checkout/${spaceId}`,
    search: window.location.search
  });
};

export const navigateToExpressLoading = () => {
  history.push({
    pathname: '/express/loading',
    search: window.location.search
  });
};

export const navigateToDirectPackagePurchase = (packageSku) => {
  history.push({
    pathname: '/direct',
    search: `packageSku=${packageSku}`
  });
};

export const navigateToResetPassword = () => {
  history.push({
    pathname: '/reset-password',
    search: window.location.search
  });
};

export const navigateToTalkablePostPurchase = () => {
  history.push({
    pathname: talkablePostPurchaseStandaloneUrl,
    search: window.location.search
  });
};

export const navigateToTalkableDashboard = () => {
  history.push({
    pathname: talkableDashboardUrl,
    search: window.location.search
  });
};

export const navigateToRenovationPackages = () => {
  window.location.href = renovationPackagesUrl;
};

export const navigateToShop = () => {
  let hostname = ''; // if empty, browser will insert the current hostname for us.
  if (window.location.hostname === 'localhost') {
    // for local dev, nextjs app runs on port 3001
    hostname = 'http://0.0.0.0:3001';
  }

  // If the pathname starts with shop we will use it as is because
  // shop includes multi-path URLs.
  let pathname = window.location.pathname;
  if (!pathname.startsWith(shopCatalogUrl)) {
    pathname = shopCatalogUrl;
  }

  // Shop is now hosted on nextjs app so we need to reload the page.
  const url = `${hostname}${pathname}${window.location.search}`;
  window.location.href = url;
};

export const navigateToShopCart = () => {
  const pathname = `${shopCatalogUrl}/cart`;

  history.push({
    pathname,
    search: window.location.search
  });
};

export const navigateToNextJSApp = ({
  path = '/design-ideas',
  search = ''
} = {}) => {
  // NextJS is a separate app so we need to do a full
  // page refresh. CDN will then send back the nextjs app.
  let hostname = ''; // if empty, browser will insert the current hostname for us.
  if (window.location.hostname === 'localhost') {
    // for local dev, nextjs runs on port 3001
    hostname = 'http://0.0.0.0:3001';
  }

  const url = `${hostname}${path}${search}`;
  window.location.href = url;
};

export const navigateToBuyGiftCard = () => {
  history.push({
    pathname: buyGiftCardUrl,
    search: window.location.search
  });
};

export const navigateToGiftCardInfo = () => {
  history.push({
    pathname: giftCardInfo,
    search: window.location.search
  });
};

export const navigateToGiftCardThankYou = () => {
  history.push({
    pathname: giftCardThankYouUrl,
    search: window.location.search
  });
};
